import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, X, CheckCircle2, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

/**
 * Universal "Add to your plan" dialog for any add-on.
 * Handles both states:
 * - User has active subscription → one-click add via /billing/add-addon (prorated)
 * - User has NO subscription → CTA to /pricing
 */
export default function AddAddonDialog({ addon, open, onClose, onAdded }) {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setDone(false);
    api.get("/billing/subscription")
      .then((r) => setSub(r.data))
      .catch(() => setSub({ active: false }))
      .finally(() => setLoading(false));
  }, [open]);

  if (!open || !addon) return null;

  async function add() {
    setAdding(true);
    try {
      const r = await api.post("/billing/add-addon", { addon_id: addon.id });
      toast.success(`${r.data.addon_name} added to your plan`);
      setDone(true);
      if (onAdded) onAdded();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not add add-on");
    } finally {
      setAdding(false);
    }
  }

  const hasActiveSub = !!sub?.active;
  const isTrialing = sub?.status === "trialing";

  return (
    <div
      data-testid="add-addon-dialog"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={() => !adding && onClose()}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl fade-in-up"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#C07A60]">Add-on</p>
            <h3 className="mt-1 font-heading font-semibold text-xl text-[#1C211F]">
              {addon.name}
            </h3>
            <p className="mt-1 text-sm text-[#57605A]">
              ${addon.amount}/mo · {addon.description || ""}
            </p>
          </div>
          <button
            data-testid="add-addon-close-btn"
            onClick={onClose}
            className="text-[#57605A] hover:text-[#1C211F]"
            aria-label="Close"
          >
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        {loading && (
          <div className="py-8 flex items-center justify-center">
            <Loader2 className="w-5 h-5 text-[#2E5C50] animate-spin" />
          </div>
        )}

        {!loading && done && (
          <div className="mt-5 text-center py-4">
            <div className="w-12 h-12 bg-[#E6F0EB] rounded-full flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-7 h-7 text-[#2E5C50]" strokeWidth={1.5} />
            </div>
            <h4 className="mt-3 font-heading font-semibold text-lg text-[#1C211F]">
              {addon.name} is active
            </h4>
            <p className="mt-2 text-sm text-[#57605A]">
              Features unlocked instantly. Pro-rated charge applied to your next invoice.
            </p>
            <button
              data-testid="add-addon-done-btn"
              onClick={onClose}
              className="mt-5 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md px-5 py-2 text-sm font-medium"
            >
              Got it
            </button>
          </div>
        )}

        {!loading && !done && !hasActiveSub && (
          <div className="mt-5 text-center py-4">
            <div className="w-12 h-12 bg-[#FFF4E5] rounded-full flex items-center justify-center mx-auto">
              <Sparkles className="w-7 h-7 text-[#C07A60]" strokeWidth={1.5} />
            </div>
            <h4 className="mt-3 font-heading font-semibold text-base text-[#1C211F]">
              Pick a plan first
            </h4>
            <p className="mt-2 text-sm text-[#57605A]">
              Add-ons attach to a Nexari plan. Start a 7-day free trial — no charge until day 8.
            </p>
            <Link
              to="/pricing"
              data-testid="add-addon-goto-pricing-btn"
              onClick={onClose}
              className="mt-5 inline-flex items-center gap-2 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md px-5 py-2 text-sm font-medium"
            >
              See plans <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        )}

        {!loading && !done && hasActiveSub && (
          <div className="mt-5 space-y-4">
            <div className="bg-[#F9F7F4] border border-[#E8E6E1] rounded-md p-4 text-sm">
              <p className="text-[#57605A] flex items-center justify-between">
                Current plan total <strong className="text-[#1C211F]">${sub.total_monthly?.toFixed(2) || "0.00"}/mo</strong>
              </p>
              <p className="mt-1.5 text-[#57605A] flex items-center justify-between">
                + {addon.name} <strong className="text-[#2E5C50]">${addon.amount.toFixed(2)}/mo</strong>
              </p>
              <div className="border-t border-[#E8E6E1] my-2.5"></div>
              <p className="text-[#1C211F] flex items-center justify-between font-semibold">
                New total <strong>${((sub.total_monthly || 0) + addon.amount).toFixed(2)}/mo</strong>
              </p>
            </div>

            <p className="text-xs text-[#57605A]">
              {isTrialing
                ? "You are still in your free trial — no charge until trial ends."
                : "You will be charged a pro-rated amount today for the remainder of this billing cycle."}
            </p>

            <button
              data-testid="add-addon-confirm-btn"
              onClick={add}
              disabled={adding}
              className="w-full flex items-center justify-center gap-2 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md py-2.5 text-sm font-medium disabled:opacity-60"
            >
              {adding && <Loader2 className="w-4 h-4 animate-spin" />}
              {isTrialing ? `Add ${addon.name}` : `Add to plan — $${addon.amount.toFixed(2)}/mo`}
            </button>
            <p className="text-[10px] text-[#57605A] text-center">
              Manageable anytime from Billing → Manage billing.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
